import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/api/client'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { RefreshCw, Save, AlertTriangle, Database } from 'lucide-react'

// Form-only schema for the settings actually edited here. Mirrors
// UpdateAiSettingsRequestSchema from @natalan/capehost-contracts field-for-field,
// but every field is required (not `.optional()`) because this form always
// submits the full settings object, not a partial patch.
const aiSettingsFormSchema = z.object({
  model: z.string().min(1, 'Select a model.'),
  max_completion_tokens: z.number().min(1).max(100000),
  temperature: z.union([z.number().min(0).max(2), z.null()]),
  system_prompt: z.string(),
  suggestion_prompt: z.string(),
  auto_generate_replies: z.boolean(),
})

type AISettingsFormValues = z.infer<typeof aiSettingsFormSchema>

const DEFAULT_SETTINGS: AISettingsFormValues = {
  model: 'gpt-5-nano',
  max_completion_tokens: 1000,
  temperature: null,
  system_prompt: '',
  suggestion_prompt: '',
  auto_generate_replies: true,
}

// Available OpenAI models
const AVAILABLE_MODELS = [
  { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
  { value: 'gpt-5', label: 'GPT-5' },
  { value: 'gpt-5.1', label: 'GPT-5.1' },
  { value: 'gpt-5.2', label: 'GPT-5.2' },
  { value: 'o1-mini', label: 'O1 Mini' },
  { value: 'o1', label: 'O1' },
  { value: 'o3-mini', label: 'O3 Mini' },
  { value: 'o3', label: 'O3' },
]

export function AISettings() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(true)
  const [isMigrating, setIsMigrating] = useState(false)

  const form = useForm<AISettingsFormValues>({
    resolver: zodResolver(aiSettingsFormSchema),
    defaultValues: DEFAULT_SETTINGS,
  })

  // Prompts missing (empty or not set) drives the migration banner. Watching
  // just these two fields (rather than the whole form) keeps banner-visibility
  // re-renders scoped to the fields that actually affect it.
  const [systemPrompt, suggestionPrompt] = useWatch({
    control: form.control,
    name: ['system_prompt', 'suggestion_prompt'],
  })
  const arePromptsMissing =
    !systemPrompt ||
    !suggestionPrompt ||
    systemPrompt.trim() === '' ||
    suggestionPrompt.trim() === ''

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const data = (await api.getAISettings()) as Partial<AISettingsFormValues>
      form.reset({ ...form.getValues(), auto_generate_replies: true, ...data })
    } catch (error: unknown) {
      toast({
        title: 'Failed to load AI settings',
        description: error instanceof Error ? error.message : 'Could not load AI settings',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const run = async () => {
      await loadSettings()
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRunMigration = async () => {
    try {
      setIsMigrating(true)
      const result = (await api.runSeedPromptsMigration()) as {
        success: boolean
        message?: string
      }

      if (result.success) {
        toast({
          title: 'Migration completed',
          description: result.message || 'Default prompts have been seeded into KV storage',
        })
        // Reload settings to show the new prompts
        await loadSettings()
      } else {
        toast({
          title: 'Migration failed',
          description: result.message || 'Could not seed prompts',
          variant: 'destructive',
        })
      }
    } catch (error: unknown) {
      toast({
        title: 'Migration failed',
        description: error instanceof Error ? error.message : 'Could not run migration',
        variant: 'destructive',
      })
    } finally {
      setIsMigrating(false)
    }
  }

  const onSubmit = async (values: AISettingsFormValues) => {
    try {
      const updated = (await api.updateAISettings(values)) as Partial<AISettingsFormValues>
      form.reset({ ...values, auto_generate_replies: true, ...updated })
      toast({
        title: 'Settings saved',
        description: 'AI settings have been updated successfully',
      })
    } catch (error: unknown) {
      toast({
        title: 'Failed to save settings',
        description: error instanceof Error ? error.message : 'Could not save AI settings',
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <LoadingSkeleton className="h-6 w-32" />
          <LoadingSkeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <LoadingSkeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Migration Warning Banner */}
      {!isLoading && arePromptsMissing && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                  Prompts Not Found in KV Storage
                </h3>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-4">
                  The system prompt and suggestion prompt are not set in KV storage. Run the
                  migration to seed default prompts from the markdown files, or manually enter them
                  below.
                </p>
                <Button
                  type="button"
                  onClick={handleRunMigration}
                  disabled={isMigrating}
                  size="sm"
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  {isMigrating ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Running Migration...
                    </>
                  ) : (
                    <>
                      <Database className="mr-2 h-4 w-4" />
                      Run Migration to Seed Default Prompts
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>AI Model Configuration</CardTitle>
          <CardDescription>
            Configure the AI model and parameters used for generating suggestion replies.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-6">
              {/* Model Selection */}
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="flex h-10 w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                      >
                        {AVAILABLE_MODELS.map(model => (
                          <option key={model.value} value={model.value}>
                            {model.label}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormDescription>
                      The AI model used for generating suggestion replies. Some models may have
                      different capabilities and costs.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Max Completion Tokens */}
              <FormField
                control={form.control}
                name="max_completion_tokens"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Completion Tokens</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="100000"
                        name={field.name}
                        ref={field.ref}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={e => field.onChange(parseInt(e.target.value, 10) || 1000)}
                      />
                    </FormControl>
                    <FormDescription>
                      Maximum number of tokens the model can generate in a single response. Higher
                      values allow longer responses but increase cost. Default: 1000 (~750 words).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Auto-generate replies */}
              <FormField
                control={form.control}
                name="auto_generate_replies"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <FormLabel>Auto-generate Suggested Replies</FormLabel>
                      <FormDescription className="mt-1">
                        When enabled, an AI reply suggestion is generated automatically for every
                        incoming guest message. Turn this off to stop LLM calls if suggestions
                        aren&apos;t being used &mdash; incoming messages and webhooks are still
                        stored as normal.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={field.value}
                        aria-label="Auto-generate Suggested Replies"
                        onClick={() => field.onChange(!field.value)}
                        className={cn(
                          'relative inline-flex h-8 w-[46px] shrink-0 cursor-pointer rounded-full border-2 border-transparent p-0.5 transition-colors',
                          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                          field.value ? 'bg-mint' : 'bg-surface2'
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'pointer-events-none inline-block size-6 rounded-full bg-white shadow-xs transition-[transform]',
                            field.value ? 'translate-x-[18px]' : 'translate-x-0'
                          )}
                        />
                      </button>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Temperature */}
              <FormField
                control={form.control}
                name="temperature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Temperature</FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="2"
                          step="0.1"
                          name={field.name}
                          ref={field.ref}
                          value={field.value ?? ''}
                          onBlur={field.onBlur}
                          onChange={e =>
                            field.onChange(
                              e.target.value === '' ? null : parseFloat(e.target.value)
                            )
                          }
                          placeholder="Not set (model default)"
                        />
                      </FormControl>
                      {field.value !== null && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => field.onChange(null)}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <FormDescription>
                      Controls randomness (0-2). Lower values make responses more focused and
                      deterministic. Higher values make responses more creative. Leave empty to use
                      model default. Note: Some models (like o1, o3) don&apos;t support temperature.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* System Prompt */}
              <FormField
                control={form.control}
                name="system_prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>System Prompt</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="Enter system prompt..."
                        className="flex min-h-[120px] w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg resize-y"
                        rows={5}
                      />
                    </FormControl>
                    <FormDescription>
                      The system prompt that guides the AI&apos;s behavior when generating
                      suggestion replies. This sets the tone and style for all generated
                      suggestions.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Suggestion Prompt */}
              <FormField
                control={form.control}
                name="suggestion_prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Suggestion Prompt Template</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="Enter suggestion prompt template..."
                        className="flex min-h-[200px] w-full rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text font-mono transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg resize-y"
                        rows={10}
                      />
                    </FormControl>
                    <FormDescription>
                      The main prompt template used for generating suggestion replies. Supports
                      template variables like{' '}
                      <code className="text-xs bg-surface px-1 py-0.5 rounded">
                        &#123;&#123;propertyContext&#125;&#125;
                      </code>
                      ,{' '}
                      <code className="text-xs bg-surface px-1 py-0.5 rounded">
                        &#123;&#123;reservationContext&#125;&#125;
                      </code>
                      ,{' '}
                      <code className="text-xs bg-surface px-1 py-0.5 rounded">
                        &#123;&#123;conversationHistory&#125;&#125;
                      </code>
                      ,{' '}
                      <code className="text-xs bg-surface px-1 py-0.5 rounded">
                        &#123;&#123;messageFrom&#125;&#125;
                      </code>
                      , and{' '}
                      <code className="text-xs bg-surface px-1 py-0.5 rounded">
                        &#123;&#123;messageText&#125;&#125;
                      </code>
                      .
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t border-border">
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Settings
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={loadSettings}
                  disabled={form.formState.isSubmitting}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
